import { ChangeEvent, useRef, useState } from "react";
import {
  ExternalLink,
  FileCheck,
  FileText,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  BusinessEntity,
  useMarkDocumentViewed,
  useRejectDocument,
  useUploadBusinessDocuments,
} from "@/hooks/useBusinesses";

interface Props {
  b: BusinessEntity;
}

const STATUS_STYLES: Record<string, string> = {
  PENDING: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  APPROVED:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

export const DocumentsTab = ({ b }: Props) => {
  const markViewed = useMarkDocumentViewed();
  const rejectDoc = useRejectDocument();
  const upload = useUploadBusinessDocuments();
  const inputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState("OTHER_CERTIFICATE");
  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const docs = b.documents ?? [];

  const ACCEPTED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/tiff",
    "application/pdf",
  ];

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const invalid = files.find((f) => !ACCEPTED_TYPES.includes(f.type));
    if (invalid) {
      toast.error(
        `"${invalid.name}" is not an allowed file type. Accepted: JPEG, PNG, WebP, TIFF, PDF.`,
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    try {
      await upload.mutateAsync({
        id: b.id,
        files,
        metadata: files.map(() => ({ docType })),
      });
      toast.success(`Uploaded ${files.length} document(s)`);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleView = (docId: string) => {
    markViewed.mutate(
      { id: b.id, docId },
      {
        onSuccess: () => toast.success("Document marked as viewed"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleReject = (docId: string) => {
    rejectDoc.mutate(
      { id: b.id, docId, note: rejectNote.trim() || undefined },
      {
        onSuccess: () => {
          toast.success("Document rejected");
          setRejectingDocId(null);
          setRejectNote("");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const cancelReject = () => {
    setRejectingDocId(null);
    setRejectNote("");
  };

  const allReviewed =
    docs.length > 0 &&
    docs.every((d) => !!d.viewedAt || d.docStatus === "REJECTED");
  const rejectedCount = docs.filter((d) => d.docStatus === "REJECTED").length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className="text-sm px-2 py-1.5 border border-border rounded-btn bg-background"
        >
          {[
            "GST_CERTIFICATE",
            "PAN_CARD",
            "FSSAI_LICENCE",
            "TRADE_LICENCE",
            "SHOP_ESTABLISHMENT",
            "AADHAAR_CARD",
            "MEDICAL_REGISTRATION",
            "MEDICAL_QUALIFICATION",
            "OTHER_CERTIFICATE",
          ].map((t) => (
            <option key={t} value={t}>
              {t.replaceAll("_", " ")}
            </option>
          ))}
        </select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          <Upload className="w-4 h-4 mr-1.5" />
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept="image/jpeg,image/png,image/webp,image/tiff,application/pdf"
          onChange={onUpload}
        />
      </div>

      {docs.length > 0 ? (
        <div
          className={`text-sm p-2 rounded border ${
            allReviewed && rejectedCount === 0
              ? "border-green-500/30 bg-green-500/5 text-green-700 dark:text-green-400"
              : allReviewed && rejectedCount > 0
                ? "border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400"
                : "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400"
          }`}
        >
          {allReviewed ? (
            <>
              <FileCheck className="w-4 h-4 inline mr-1" />
              {rejectedCount > 0
                ? `${rejectedCount} document(s) rejected — business cannot be approved until resubmitted.`
                : "All documents reviewed — business can be approved."}
            </>
          ) : (
            <>
              {
                docs.filter((d) => !d.viewedAt && d.docStatus !== "REJECTED")
                  .length
              }{" "}
              of {docs.length} documents still need review.
            </>
          )}
        </div>
      ) : null}

      {docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No documents uploaded yet.
        </p>
      ) : (
        <div className="bg-card rounded-card border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground uppercase">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Label</th>
                <th className="px-3 py-2 text-left font-medium">Doc number</th>
                <th className="px-3 py-2 text-left font-medium">Expires</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <>
                  <tr key={d.id} className="border-t border-border">
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                        {d.docType}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {d.label ?? "—"}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {d.docNumber ?? "—"}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {d.expiryDate
                        ? new Date(d.expiryDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[d.docStatus ?? "PENDING"]}`}
                      >
                        {d.docStatus ?? "PENDING"}
                      </span>
                      {d.updatedAt && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatDistanceToNow(new Date(d.updatedAt), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                      {!d.updatedAt && d.viewedAt && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                          viewed{" "}
                          {formatDistanceToNow(new Date(d.viewedAt), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right space-x-2">
                      <a
                        href={d.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                      <Button
                        size="sm"
                        variant={d.viewedAt ? "outline" : "default"}
                        disabled={!!d.viewedAt || markViewed.isPending}
                        onClick={() => handleView(d.id)}
                      >
                        {d.viewedAt ? "Viewed" : "Mark viewed"}
                      </Button>
                      {d.docStatus !== "REJECTED" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={rejectDoc.isPending}
                          onClick={() => setRejectingDocId(d.id)}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Reject
                        </Button>
                      )}
                    </td>
                  </tr>
                  {rejectingDocId === d.id && (
                    <tr
                      key={`${d.id}-reject`}
                      className="border-t border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/10"
                    >
                      <td colSpan={6} className="px-3 py-3">
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs text-muted-foreground mb-1">
                              Rejection note (optional)
                            </label>
                            <textarea
                              value={rejectNote}
                              onChange={(e) => setRejectNote(e.target.value)}
                              placeholder="Reason for rejecting this document…"
                              rows={2}
                              className="w-full text-sm px-2 py-1.5 border border-border rounded bg-background resize-none"
                            />
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={rejectDoc.isPending}
                              onClick={() => handleReject(d.id)}
                            >
                              Confirm reject
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelReject}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                        {d.rejectionNote && (
                          <p className="mt-2 text-xs text-red-700 dark:text-red-400">
                            Previous note: {d.rejectionNote}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {d.docStatus === "REJECTED" &&
                    d.rejectionNote &&
                    rejectingDocId !== d.id && (
                      <tr
                        key={`${d.id}-note`}
                        className="bg-red-50/30 dark:bg-red-950/5"
                      >
                        <td colSpan={6} className="px-3 pb-2">
                          <p className="text-xs text-red-700 dark:text-red-400">
                            Note: {d.rejectionNote}
                          </p>
                        </td>
                      </tr>
                    )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
